import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Sparkles, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface NameModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEditing?: boolean;
}

export const NameModal: React.FC<NameModalProps> = ({ isOpen, onClose, isEditing = false }) => {
  const { updateUserProfile } = useAuth();
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditing && !name.trim()) return;

    setIsSubmitting(true);
    
    // Simulate a brief processing time for better UX
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Update the user profile with the new name
    updateUserProfile({ displayName: name.trim() });
    
    setIsSuccess(true);
    
    // Close modal after showing success state
    setTimeout(() => {
      setIsSuccess(false);
      onClose();
      setName('');
    }, 1500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (isEditing) {
        // Pre-fill with current name if editing
        const currentProfile = JSON.parse(localStorage.getItem('ecstatic_guest_profile') || '{}');
        setName(currentProfile.displayName || '');
      } else {
        setName('');
      }
      setIsSubmitting(false);
      setIsSuccess(false);
    }
  }, [isOpen, isEditing]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
            style={{ pointerEvents: 'auto' }}
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto"
            onKeyDown={handleKeyPress}
          >
            <div className="relative w-full max-w-md mx-auto">
              <motion.div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                layout
              >
                {/* Header */}
                <div className="relative bg-gradient-to-r from-purple-500 to-blue-500 p-6 text-white">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 rounded-full">
                      <User className="h-6 w-6" />
                    </div>
                    <div>
                                           <h2 className="text-xl font-bold">
                       {isEditing ? 'Edit Your Name' : 'Welcome to EcstaticLearn!'}
                     </h2>
                     <p className="text-purple-100 text-sm mt-1">
                       {isEditing ? 'Update your display name' : 'Let\'s personalize your learning experience'}
                     </p>
                    </div>
                  </div>
                  
                  {/* Decorative elements */}
                  <div className="absolute top-2 right-2">
                    <Sparkles className="h-4 w-4 text-purple-200 animate-pulse" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {!isSuccess ? (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          What should we call you?
                        </label>
                        <div className="relative">
                          <input
                            id="name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter your name..."
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200"
                            autoFocus
                            disabled={isSubmitting}
                            maxLength={30}
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <User className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          This will be displayed on your dashboard
                        </p>
                      </div>

                      <div className="flex space-x-3">
                                                 <button
                           type="button"
                           onClick={onClose}
                           disabled={isSubmitting}
                           className="flex-1 px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                         >
                           {isEditing ? 'Cancel' : 'Skip for now'}
                         </button>
                                                 <button
                           type="submit"
                           disabled={(!isEditing && !name.trim()) || isSubmitting}
                          className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
                        >
                          {isSubmitting ? (
                            <div className="flex items-center justify-center space-x-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>Saving...</span>
                            </div>
                                                     ) : (
                             isEditing ? 'Update Name' : 'Save Name'
                           )}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-8"
                    >
                      <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                        <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Welcome, {name}!
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Your profile has been updated successfully.
                      </p>
                    </motion.div>
                  )}
                </div>

                {/* Footer */}
                {!isSuccess && (
                  <div className="px-6 pb-6">
                    <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      You can always change this later in your profile settings
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}; 